/**
 * The Berth station tab (M8 plan MC-b6a): store goods, wares and components from the hold in this station's berth
 * and take them back, and see why a row is refused (a refused kind, a cap, a full hold, a frozen or relocating berth).
 * Thin: the rules live in `ui/models/berth.ts`; the actions are `UiActions.craft` commands (`berth.store`,
 * `berth.take`) that `features/berth/feature.ts` registers and wires here.
 *
 * Mounted as a `StationServicePanel` (`berth`, every station) in M2's station screen through
 * `bridge/m2/stationTabs.ts`, like the fleet panels. It re-renders on every store change (the career after an
 * action) and on new system views. UI events only.
 */
import type { ReactNode } from 'react'
import type { CraftCommand } from '../../craft/hook'
import type { StationSite } from '../../galaxy/layout/types'
import type { PanelContext, StationServicePanel } from '../../reach/ports'
import type { CmdOutcome } from '../contracts'
import { FocusButton } from '../focus'
import { mountReact } from '../strategic/mount'
import { BERTH_UI, berthModel, type BerthModel, type BerthModelInput, type BerthRow } from '../models/berth'
import '../strategic/strategic.css'
import '../fleet/stationPanels.css'

export interface BerthProps {
  model: BerthModel
  status: string | null
  onStore: (row: BerthRow) => void
  onTake: (row: BerthRow) => void
}

/** The focus index of each row's button: only rows with an action have one (a refused kind has none, PF-5). */
function buttonIndices(rows: readonly BerthRow[], first: number): number[] {
  let n = first
  return rows.map((r) => (r.action ? n++ : -1))
}

function Rows(p: { rows: BerthRow[]; side: 'store' | 'take'; first: number; label: string; onAct: (row: BerthRow) => void }): ReactNode {
  const index = buttonIndices(p.rows, p.first)
  return p.rows.map((r, i) => (
    <div key={r.key} className="fp-row" data-testid={`berth-${p.side}-row-${r.key}`} data-blocked={r.enabled ? undefined : ''}>
      <div>
        <div className="fp-row__name">{r.name}</div>
      </div>
      <div className="fp-row__price ui-num">{r.amountLabel}</div>
      {r.reason !== '' ? (
        <div className="fp-row__reason" data-testid={`berth-${p.side}-reason-${r.key}`}>
          {r.reason}
        </div>
      ) : null}
      {r.action ? (
        <div className="fp-row__actions">
          <FocusButton group="berth" index={index[i]} className="ui-btn--small" testId={`berth-${p.side}-${r.key}`} disabled={!r.enabled} onActivate={() => p.onAct(r)}>
            {p.label} {r.name}
          </FocusButton>
        </div>
      ) : null}
    </div>
  ))
}

export function Berth(p: BerthProps): ReactNode {
  const m = p.model
  return (
    <div className="sui fp-panel" data-testid="berth-panel" data-access={m.access}>
      <header className="sui-head">
        <h2 className="sui-title">{m.title}</h2>
        <p className="sui-sub">{m.sub}</p>
      </header>
      <p className="fp-meta">
        <span data-testid="berth-state">{m.stateLabel}</span>
        <span data-testid="berth-components">{m.componentsLabel}</span>
      </p>
      {m.closedReason !== null ? (
        <p className="fp-row__reason" data-testid="berth-closed">
          {m.closedReason}
        </p>
      ) : null}
      <h3 className="sui-sub">{m.berthHeading}</h3>
      {m.storedEmpty !== null ? <p className="sui-empty">{m.storedEmpty}</p> : null}
      <Rows rows={m.stored} side="take" first={0} label={m.takeLabel} onAct={p.onTake} />
      <h3 className="sui-sub">{m.holdHeading}</h3>
      {m.holdEmpty !== null ? <p className="sui-empty">{m.holdEmpty}</p> : null}
      <Rows rows={m.hold} side="store" first={m.stored.filter((r) => r.action).length} label={m.storeLabel} onAct={p.onStore} />
      {p.status !== null ? (
        <p className="fp-status" role="status" data-testid="berth-status">
          {p.status}
        </p>
      ) : null}
    </div>
  )
}

/** What the panel reads and runs (`features/berth/feature.ts` builds it from the career runtime and the strategic client). */
export interface BerthPanelDeps {
  /** The model input for `site`'s berth, or null while no career runs. */
  input(site: StationSite): BerthModelInput | null
  /** Runs a craft command (`UiActions.craft`). */
  run(cmd: CraftCommand): CmdOutcome
  /** Calls `fn` whenever the input may have changed (the UI store, new system views); returns the unsubscribe. */
  subscribe(fn: () => void): () => void
}

/** The Berth tab (`berth`, every station). */
export function createBerthPanel(deps: BerthPanelDeps): StationServicePanel {
  return {
    id: 'berth',
    title: BERTH_UI.text.title,
    order: BERTH_UI.order,
    available: (): boolean => true,
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      const site = ctx.station
      const mount = mountReact(el, 'sui-mount')
      let status: string | null = null
      let alive = true
      const act = (kind: 'berth.store' | 'berth.take', row: BerthRow): void => {
        const input = deps.input(site)
        if (input === null) return
        const r = deps.run({ kind, stationId: input.stationId, sel: row.sel })
        status = r.ok ? (kind === 'berth.store' ? BERTH_UI.text.stored : BERTH_UI.text.taken) : r.error.message
        render()
      }
      function render(): void {
        if (!alive) return
        const input = deps.input(site)
        const model = input === null ? null : berthModel(input)
        mount.render(
          model === null ? (
            <div className="sui fp-panel" data-testid="berth-panel">
              <p className="sui-empty">{BERTH_UI.text.noCareer}</p>
            </div>
          ) : (
            <Berth model={model} status={status} onStore={(r) => act('berth.store', r)} onTake={(r) => act('berth.take', r)} />
          ),
        )
        ctx.refresh()
      }
      const off = deps.subscribe(render)
      render()
      return (): void => {
        alive = false
        off()
        mount.unmount()
      }
    },
  }
}
